import { useMemo } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import { parseDecimal, type FundingPayment } from "@/backend/types";
import { Spinner } from "@/components/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Wallet } from "lucide-react";
import { useI18n } from "@/features/i18n";

type FundingTabProps = {
  fundingPayments: FundingPayment[];
  isLoading: boolean;
};

const formatPercentage = (rate: number) => {
  return `${(rate * 100).toFixed(4)}%`;
};

export const FundingTab = ({ fundingPayments, isLoading }: FundingTabProps) => {
  const { formatTradeDate } = useI18n();

  // Calculate funding summary stats
  const fundingSummary = useMemo(() => {
    let totalReceived = 0;
    let totalPaid = 0;

    fundingPayments.forEach((payment) => {
      const amount = parseDecimal(payment.payment);
      if (amount >= 0) {
        totalReceived += amount;
      } else {
        totalPaid += Math.abs(amount);
      }
    });

    return {
      totalReceived,
      totalPaid,
      net: totalReceived - totalPaid,
    };
  }, [fundingPayments]);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  if (!fundingPayments.length) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Wallet className="size-12 mb-3 opacity-50" />
          <p className="text-sm font-medium">No funding payments yet</p>
          <p className="text-xs text-center mt-1">
            Funding payments will appear here when you hold positions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none overflow-hidden">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b border-border/50">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Funding History</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fundingPayments.length} payment{fundingPayments.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 md:px-6">
        <div className="rounded-xl bg-muted/30 p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Received</div>
          <div className="text-xl font-bold text-bid-foreground">
            +${formatCurrency(fundingSummary.totalReceived, 2)}
          </div>
        </div>
        <div className="rounded-xl bg-muted/30 p-4">
          <div className="text-xs text-muted-foreground mb-1">Total Paid</div>
          <div className="text-xl font-bold text-ask-foreground">
            -${formatCurrency(fundingSummary.totalPaid, 2)}
          </div>
        </div>
        <div className="rounded-xl bg-muted/30 p-4">
          <div className="text-xs text-muted-foreground mb-1">Net Funding</div>
          <div
            className={cn(
              "text-xl font-bold",
              fundingSummary.net >= 0 ? "text-bid-foreground" : "text-ask-foreground"
            )}
          >
            {fundingSummary.net >= 0 ? "+" : ""}${formatCurrency(Math.abs(fundingSummary.net), 2)}
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="hidden md:grid md:grid-cols-5 gap-4 px-4 md:px-6 py-3 bg-muted/30 text-xs font-medium text-muted-foreground">
        <div>Market</div>
        <div className="text-right">Position Size</div>
        <div className="text-right">Funding Rate</div>
        <div className="text-right">Payment</div>
        <div className="text-right">Date</div>
      </div>

      {/* Payments List */}
      <ScrollArea className="h-[400px]">
        <div className="divide-y divide-border/30">
          {fundingPayments.map((payment) => {
            const positionSize = parseDecimal(payment.position_size);
            const fundingRate = parseDecimal(payment.funding_rate);
            const paymentAmount = parseDecimal(payment.payment);
            const isPositive = paymentAmount >= 0;
            const timestamp = new Date(payment.created_at);

            return (
              <div
                key={payment.id}
                className="px-4 md:px-6 py-4 hover:bg-muted/20 transition-colors"
              >
                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{payment.market}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatTradeDate(timestamp)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "font-semibold",
                          isPositive ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {isPositive ? "+" : ""}${formatCurrency(Math.abs(paymentAmount), 4)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Rate: {formatPercentage(fundingRate)}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Position: {positionSize.toFixed(4)}
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-5 gap-4 items-center">
                  {/* Market */}
                  <div>
                    <span className="font-medium text-sm">{payment.market}</span>
                  </div>

                  {/* Position Size */}
                  <div className="text-sm text-right font-mono">
                    {positionSize.toFixed(4)}
                  </div>

                  {/* Funding Rate */}
                  <div
                    className={cn(
                      "text-sm text-right",
                      fundingRate >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                    )}
                  >
                    {formatPercentage(fundingRate)}
                  </div>

                  {/* Payment */}
                  <div
                    className={cn(
                      "text-sm text-right font-semibold",
                      isPositive ? "text-bid-foreground" : "text-ask-foreground"
                    )}
                  >
                    {isPositive ? "+" : ""}${formatCurrency(Math.abs(paymentAmount), 4)}
                  </div>

                  {/* Date */}
                  <div className="text-sm text-muted-foreground text-right">
                    {formatTradeDate(timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

